import React, {useState, useEffect, useContext, useRef} from "react";
import {Accordion, AccordionDetails, AccordionSummary, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Stack, Tooltip, Typography, useTheme} from "@mui/material";
import {DeleteRounded, ExpandMore, FileUploadRounded, DownloadRounded, CreateRounded} from "@mui/icons-material";
import {useFieldArray, useForm, FormProvider} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import {idEntityState, RFMContractorDataValueState} from "../model/state";
import {FormAutocomplete, FormDatePicker, FormTextField} from "../../common/form-fields-wrappers";
import {AxiosError} from "axios";
import { yupResolver } from "@hookform/resolvers/yup";
import { CONTRACTOR_DOCUMENTS_FORM_VALIDATION_SCHEMA } from "../utils/config";
import { insertDocumentsContractor, deleteDocumentDms, getDocumentDms } from "../service/contractorSearch.actions";
import DocumentalHistory from "../../documental-history/DocumentalHistory";
import AssociatedDmsList from "./AssociatedDmsList";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {setSnackbar} from "../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import CustomLoader from "../../../../util/component/CustomLoader";

const DocumentRepository = () => {
    //console.log('document contractor repo');
    const theme = useTheme();
    const typeList = ["Contract", "Warranty", "Other"];
    const [contractorDataState, setContractorDataState] = useRecoilState(RFMContractorDataValueState);
    const [isDisabled, setIsDisabled] = useState(true);
    const [getId, setId] = useRecoilState(idEntityState);
    const [openHistory, setOpenHistory] = useState(false);
    const handleOpenHistory = () => setOpenHistory(true);
    const handleCloseHistory = () => setOpenHistory(false);
    const [update, setUpdate] = useState(true);
    const { dispatchGlobalData } = useContext(GlobalContext);
    const [isFetching, setIsFetching] = useState(false);
    const MAX_UPLOAD_BYTES = 70 * 1024 * 1024;
    const fetchedDocIdsRef = useRef<Set<string | number>>(new Set());
    const deletedDocIdsRef = useRef<Set<string | number>>(new Set());
    const resetFetchedDocIds = () => {
        fetchedDocIdsRef.current.clear();
    };
    const resetDeletedDocIds = () => {
        deletedDocIdsRef.current.clear();
    };

    const handleDisableForm = (value:boolean) => {
        setIsDisabled(value);
    }

    const {t} = useTranslation(['network']);
    const documentsForm = useForm({
        defaultValues: {
            documents: contractorDataState.documents ?? []
        },
        resolver: yupResolver(CONTRACTOR_DOCUMENTS_FORM_VALIDATION_SCHEMA)
    });

    const areValuesEqual = (a: any, b: any) => {
        //console.log('areValuesEqual', a, b);
        if (a === b) return true;
        if (a instanceof Date && b instanceof Date) {
            //console.log('aredates');
            return a.getTime() === b.getTime();
        }
        return JSON.stringify(a) === JSON.stringify(b);
    };

    const markFieldAsUpserted = (index: number, field: string, newValue: any, options?: {force?: boolean}) => {
         //console.log('markFieldAsUpserted');
        const currentValue = documentsForm.getValues(`documents.${index}.${field}`);
        if (options?.force || !areValuesEqual(currentValue, newValue)) {
            //console.log('areValuesNotEqual');
            documentsForm.setValue(`documents.${index}.upserted`, true, { shouldDirty: true });
        }
    };

    const onSubmit = async (data: any) => {
        setIsFetching(true);
        try {
            for (const deletedId of Array.from(deletedDocIdsRef.current)) {
                await deleteDocumentDms(deletedId);
            }
            resetDeletedDocIds();
            const docsToUpsert = (data.documents || []).filter((doc: any) => doc?.upserted === true);
            const docsNotUpserted = (data.documents || []).filter((doc: any) => doc?.upserted !== true);
            const collectedDocuments: any[] = [];
            if (docsToUpsert.length > 0) {
                for (const doc of docsToUpsert) {
                     const { upserted, ...rest } = doc || {};
                     const payloadDocuments = [rest];
                    const resp = await insertDocumentsContractor({ id: getId, documents: payloadDocuments });
                    if (Array.isArray(resp?.documents)) {
                       collectedDocuments.push(...resp.documents.map((d: any) => ({ ...d, upserted: false })));
                    }
                }
            }
            const finalList = [
                ...docsNotUpserted.map((doc: any) => ({ ...doc, upserted: false })),
                ...collectedDocuments
            ];
            if (finalList.length > 0) {
                resetFetchedDocIds();
                setContractorDataState((prev: any) => ({ ...prev, documents: finalList }));
            }
            setIsDisabled(true);
        } catch (err) {
            dispatchGlobalData({type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('Error'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        } finally {
            setIsFetching(false);
        }
    }

    const onSubmitError = (errors: any) => {
        console.log('onSubmit errors', errors);
    }

    const documentsArray = useFieldArray({name: 'documents', control: documentsForm.control});

    const handleAddDocument = () => {
        documentsArray.append({
            title: "Other",
            file: "",
            note: "",
            fileName: "",
            validityStartDate: null,
            validityEndDate: null,
            upserted: true,
        });
    }

    const handleOnDelete = async (index?: number) => {
        if (index === undefined) return;
        const docToRemove = documentsForm.getValues(`documents.${index}`);
        if (docToRemove?.id) {
            deletedDocIdsRef.current.add(docToRemove.id);
        }
        documentsArray.remove(index);
    }

    const handleFileOnChange = (e: React.BaseSyntheticEvent, field: any, index: number) => {
        e.stopPropagation();
        if (!!e.target.files[0]?.size) {
            let reader = new FileReader();
            reader.readAsDataURL(e.target.files[0]);
            reader.onload = function () {
                if (e.target.files[0].size < MAX_UPLOAD_BYTES) {
                    documentsForm.setValue(field, reader.result, { shouldValidate: true, shouldDirty: true });
                    documentsForm.setValue(`documents.${index}.fileName`, e.target.files[0].name, { shouldValidate: true, shouldDirty: true });
                    documentsForm.setValue(`documents.${index}.upserted`, true, { shouldDirty: true });
                    documentsForm.trigger(field);
                    setUpdate(!update)
                } else {
                    dispatchGlobalData({type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t('common.error.docSize'),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                }
            };
        }
    }

    const downloadFunc = (data: string, fileName?: string) => {
        const linkSource = data;
        const downloadLink = document.createElement('a');
        downloadLink.href = linkSource;
        downloadLink.download = fileName ? fileName : 'file';
        downloadLink.click();
    };

    const downloadDocument = (base64: string | ArrayBuffer, fileName?: string) => {
        downloadFunc(base64.toString(), fileName);
    };

    const updatecomponent = () => {
        setUpdate(u => !u);
    }

    const dropChanges = () => {
        setContractorDataState((prev: any) => ({ ...prev, documents: prev.documents.map((d: any) => ({ ...d, upserted: false })) }));
        documentsArray.replace(contractorDataState.documents ?? []);
        resetDeletedDocIds();
        resetFetchedDocIds();
        setIsDisabled(true);
        updatecomponent();
    }

    useEffect(() => {
        documentsForm.setValue("documents", contractorDataState.documents ?? []);
    }, [contractorDataState.documents]);

    useEffect(() => {
        if (!contractorDataState.documents || contractorDataState.documents.length === 0) {
            setIsFetching(false);
            return;
        }

        //console.log('get file 3333');

        const docsToFetch = contractorDataState.documents
            .map((doc: any, idx: number) => ({ doc, idx }))
            .filter(({ doc }: { doc: any }) => {
                const docId = doc?.id;
                return docId && !doc.file && !fetchedDocIdsRef.current.has(docId);
            });

        if (docsToFetch.length === 0) {
            //setIsFetching(false);
            return;
        }

        setIsFetching(true);

        //const inFlight = new Set<string | number>();
        //let isCancelled = false;

        const fetchDocumentBase64 = async (docId: string | number, idx: number) => {
            if (!docId) return;
            if (fetchedDocIdsRef.current.has(docId)) return;
            //if (inFlight.has(docId)) return;
            //inFlight.add(docId);
            fetchedDocIdsRef.current.add(docId);
            try {
                const resp = await getDocumentDms(docId);
                const fileBase64 = resp?.file;
                if (fileBase64) {
                    documentsForm.setValue(`documents.${idx}.file`, fileBase64, { shouldValidate: true, shouldDirty: true });
                     documentsForm.trigger(`documents.${idx}.file`);
                    updatecomponent();
                }
            } catch (e) {
                fetchedDocIdsRef.current.delete(docId);
                console.warn('Error fetching document', docId, e);
            } finally {
                //inFlight.delete(docId);
            }
        };

        const pending = docsToFetch.map(({ doc, idx }: { doc: any; idx: number }) => fetchDocumentBase64(doc.id, idx));

        Promise.allSettled(pending).finally(() => {
            //if (!isCancelled) {
                setIsFetching(false);
            //}
        });

        return () => {
            //isCancelled = true;
            //inFlight.clear();
        };
    }, [contractorDataState.documents, update]);

    return (
        <>
            <Accordion defaultExpanded={true}>
                <AccordionSummary expandIcon={<ExpandMore/>}>
                    <Typography variant={"h6"}>
                        {t("contractorMng.managerRFM.documentRepository.accordion.contractorSection")}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <FormProvider {...documentsForm}>
                         <Grid item xs={12}>
                             <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                                    { isDisabled &&
                                        <Tooltip title={t("contractorMng.managerRFM.documentRepository.buttons.edit")}>
                                            <IconButton sx={{mx: theme.spacing(0.5)}} color={"accent"} onClick={() => handleDisableForm(false)}>
                                                <CreateRounded />
                                            </IconButton>
                                        </Tooltip>
                                    }
                                </Stack>
                            </Grid>
                            {documentsArray.fields.length === 0 &&
                                <Grid item container xs={12} md={12} alignItems="center" justifyContent="flex">
                                    <p>{t("contractorMng.managerRFM.documentRepository.messages.noDocument")}</p>
                                </Grid>
                            }
                            {documentsArray.fields.map((field, index) => (
                                <Grid key={field.id} item container md={12} xs={12}>
                                    {isDisabled === false ? (
                                        <>
                                            <Grid container item md={10} xs={10} columnSpacing={2}>
                                                <Grid item md={3} xs={3}>
                                                    <FormAutocomplete
                                                        disabled={isDisabled}
                                                        name={`documents.${index}.title`}
                                                        control={documentsForm.control}
                                                        options={typeList}
                                                        label={t("contractorMng.managerRFM.documentRepository.documentsArray.enabled.title")}
                                                        onInputChange={() => updatecomponent()}
                                                        onChange={(_, value) => markFieldAsUpserted(index, 'title', value)}
                                                    />
                                                </Grid>
                                                <Grid item md={3} xs={3}>
                                                    <FormTextField
                                                        disabled={isDisabled}
                                                        control={documentsForm.control}
                                                        name={`documents.${index}.note`}
                                                        label={t("contractorMng.managerRFM.documentRepository.documentsArray.enabled.note")}
                                                        onChange={(event) => markFieldAsUpserted(index, 'note', event.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item md={3} xs={3}>
                                                    <FormDatePicker
                                                        disabled={isDisabled}
                                                        control={documentsForm.control}
                                                        name={`documents.${index}.validityStartDate`}
                                                        label={t("contractorMng.managerRFM.documentRepository.documentsArray.enabled.validityStartDate")}
                                                        onChange={(value) => markFieldAsUpserted(index, 'validityStartDate', value, {force: true})}
                                                    />
                                                </Grid>
                                                <Grid item md={3} xs={3}>
                                                    <FormDatePicker
                                                        disabled={isDisabled}
                                                        control={documentsForm.control}
                                                        name={`documents.${index}.validityEndDate`}
                                                        label={t("contractorMng.managerRFM.documentRepository.documentsArray.enabled.validityEndDate")}
                                                        onChange={(value) => markFieldAsUpserted(index, 'validityEndDate', value, {force: true})}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={2} md={2} sx={{display: "flex", justifyContent: "center"}}>
                                                {documentsForm.getValues(`documents.${index}.file`) !== "" ? (
                                                    <IconButton color="secondary" component="label" sx={{alignSelf: "end"}}>
                                                        <input hidden type="file" onChange={(e: React.SyntheticEvent) => handleFileOnChange(e, `documents.${index}.file`, index)}/>
                                                        <FileUploadRounded/>
                                                    </IconButton>
                                                ) : (
                                                    <IconButton color="error" component="label" sx={{alignSelf: "end"}}>
                                                        <input hidden accept="image/*" type="file" onChange={(e: React.SyntheticEvent) => handleFileOnChange(e, `documents.${index}.file`, index)}/>
                                                        <FileUploadRounded/>
                                                    </IconButton>
                                                )}
                                                {documentsForm.getValues(`documents.${index}.file`) !== "" && (
                                                    <Box sx={{display: "flex", justifyContent: "flex-end", alignSelf: "end"}}>
                                                        <IconButton color="secondary" onClick={(e: React.SyntheticEvent) => downloadDocument(documentsForm.getValues(`documents.${index}.file`), documentsForm.getValues(`documents.${index}.title`))}>
                                                            <DownloadRounded/>
                                                        </IconButton>
                                                    </Box>
                                                )}
                                                <Box sx={{display: "flex", justifyContent: "flex-end", alignSelf: "end"}}>
                                                    <IconButton color="secondary" onClick={() => handleOnDelete(index)}>
                                                        <DeleteRounded/>
                                                    </IconButton>
                                                </Box>
                                            </Grid>
                                        </>
                                    ) : (
                                        <Grid container item md={12} xs={12} columnSpacing={2}>
                                            <Grid item md={3} xs={3}>
                                                <FormAutocomplete disabled={isDisabled} name={`documents.${index}.title`} control={documentsForm.control} options={typeList} label={t("contractorMng.managerRFM.documentRepository.documentsArray.enabled.title")}/>
                                            </Grid>
                                            <Grid item md={3} xs={3}>
                                                <FormTextField disabled={isDisabled} name={`documents.${index}.note`} control={documentsForm.control} label={t("contractorMng.managerRFM.documentRepository.documentsArray.enabled.note")}/>
                                            </Grid>
                                            <Grid item md={3} xs={3}>
                                                <FormDatePicker disabled={isDisabled} name={`documents.${index}.validityStartDate`} control={documentsForm.control} label={t("contractorMng.managerRFM.documentRepository.documentsArray.enabled.validityStartDate")}/>
                                            </Grid>
                                            <Grid item md={3} xs={3}>
                                                <FormDatePicker disabled={isDisabled} name={`documents.${index}.validityEndDate`} control={documentsForm.control} label={t("contractorMng.managerRFM.documentRepository.documentsArray.enabled.validityEndDate")}/>
                                            </Grid>
                                        </Grid>
                                    )}
                                </Grid>
                            ))}
                            {isDisabled === false && (
                                <Grid container sx={{display: 'flex', justifyContent: "flex-end", alignItems: "flex-end"}}>
                                    <Grid xs={12} md={6} item sx={{display: 'flex', justifyContent: {xs: 'center', md: 'flex-start'}, gap: {xs: 1, md: 2}, flexDirection: {xs: 'column', md: 'row'}, padding: {xs: 1, md: 0}}}>
                                        <Button color="accent" onClick={handleAddDocument}>
                                            {t("contractorMng.managerRFM.documentRepository.documentsArray.buttons.addDocument")}
                                        </Button>
                                        <Button onClick={handleOpenHistory}>
                                            {t("contractorMng.managerRFM.documentRepository.documentsArray.buttons.archive")}
                                        </Button>
                                    </Grid>
                                    <Grid xs={12} md={6} item sx={{display: 'flex', justifyContent: {xs: 'center', md: 'flex-end'}, gap: {xs: 1, md: 2}, flexDirection: {xs: 'column', md: 'row'}, padding: {xs: 1, md: 0}}}>
                                        <Button onClick={dropChanges}>
                                            {t("contractorMng.managerRFM.documentRepository.documentsArray.buttons.cancel")}
                                        </Button>
                                        <Button type="button" onClick={() => documentsForm.handleSubmit(onSubmit, onSubmitError)()}>
                                            {t("contractorMng.managerRFM.documentRepository.documentsArray.buttons.confirm")}
                                        </Button>
                                     </Grid>
                                 </Grid>
                             )}
                    </FormProvider>
                 </AccordionDetails>
             </Accordion>


            <Accordion defaultExpanded={true}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant={"h6"}>
                        {t("contractorMng.managerRFM.documentRepository.accordion.previousAssociatedDmsData")}
                    </Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <AssociatedDmsList list={contractorDataState.associatedDms.filter((x: any) => x.primaryDms === false)} />
                </AccordionDetails>
            </Accordion>

            <Dialog
                open={openHistory}
                onClose={handleCloseHistory}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    {t('contractorMng.managerRFM.documentRepository.dialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DocumentalHistory functionality={"CONTR"} id={contractorDataState.id} documentsTitles={typeList}/>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseHistory}>{t('contractorMng.managerRFM.documentRepository.dialog.buttons.cancel')}</Button>
                </DialogActions>
            </Dialog>
            <CustomLoader loading={isFetching}/>
        </>
    )
}

export default DocumentRepository;

